import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { useMobile } from '../../context/MobileContext';
import { formatGamePeriod } from '../../utils/gameDetailUtils';
import { useWeekLockVisibility } from '../../hooks/useWeekLockVisibility';
import GameDetailOverview from './GameDetailOverview';
import GameDetailGameLog from './GameDetailGameLog';
import GameDetailSplits from './GameDetailSplits';
import GameDetailLines from './GameDetailLines';
import GameDetailBoxScore from './GameDetailBoxScore';
import GameDetailWeather from './GameDetailWeather';
import GameDetailLocks from './GameDetailLocks';
import GameDetailPickGrid from './GameDetailPickGrid';
import styles from './GameDetailModal.module.css';

const DESKTOP_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'gamelog', label: 'Game Log' },
  { id: 'splits', label: 'Splits' },
  { id: 'lines', label: 'Lines' },
  { id: 'box', label: 'Box Score' },
  { id: 'weather', label: 'Weather' },
];

const GameDetailModal = ({
  game,
  displayedWeek,
  isOpen,
  onClose,
  onPickAction,
  hasReachedMaxPicks = false,
  initialSelectedPick = null,
  previewLocks = null,
  previewMode = false,
  previewMatchupStats = null,
  previewLineHistory = null,
}) => {
  const { locksCache, user, isFutureWeekLockAllowed, watchedPicks } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { canSeeLocks } = useLeagueSettings();
  const lockVisibility = useWeekLockVisibility(displayedWeek);
  const { isMobile } = useMobile();

  const [selectedPick, setSelectedPick] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [futureWeekLockAllowed, setFutureWeekLockAllowed] = useState(true);
  const [isCheckingFutureWeek, setIsCheckingFutureWeek] = useState(false);
  const [matchupStats, setMatchupStats] = useState(null);
  const [matchupStatsLoading, setMatchupStatsLoading] = useState(false);
  const [matchupStatsError, setMatchupStatsError] = useState(null);
  const [lineHistory, setLineHistory] = useState(null);
  const [lineHistoryLoading, setLineHistoryLoading] = useState(false);
  const [lineHistoryError, setLineHistoryError] = useState(null);

  const resolvedMatchupStats = previewMode ? previewMatchupStats : matchupStats;
  const resolvedLineHistory = previewMode ? previewLineHistory : lineHistory;

  const isGameLockable = game?.game_status === 'scheduled';

  const gameLocks = useMemo(() => {
    if (previewLocks) return previewLocks;
    return locksCache[displayedWeek]?.filter((lock) => lock.gameId === game?.id) || [];
  }, [previewLocks, locksCache, displayedWeek, game?.id]);

  const showGameLocks = previewMode
    ? gameLocks.length > 0
    : lockVisibility.shouldShowLocksSection(gameLocks.length > 0);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPick(null);
      setActiveTab('overview');
      return;
    }

    if (initialSelectedPick && isGameLockable) {
      const lineValue = initialSelectedPick === 'away'
        ? (game.betting?.spread != null ? -game.betting.spread : null)
        : initialSelectedPick === 'home'
          ? game.betting.spread
          : game.betting?.over_under;

      if (lineValue != null) {
        setSelectedPick({ pickType: initialSelectedPick, lockLine: lineValue });
      } else {
        setSelectedPick(null);
      }
    } else {
      setSelectedPick(null);
    }
  }, [isOpen, initialSelectedPick, game, isGameLockable]);

  useEffect(() => {
    if (previewMode) {
      setFutureWeekLockAllowed(true);
      setIsCheckingFutureWeek(false);
      return undefined;
    }

    const checkFutureWeekLock = async () => {
      setIsCheckingFutureWeek(true);
      try {
        const allowed = await isFutureWeekLockAllowed(displayedWeek);
        setFutureWeekLockAllowed(allowed);
      } catch {
        setFutureWeekLockAllowed(false);
      } finally {
        setIsCheckingFutureWeek(false);
      }
    };

    if (isOpen) {
      checkFutureWeekLock();
    }
    return undefined;
  }, [displayedWeek, isFutureWeekLockAllowed, isOpen, previewMode]);

  useEffect(() => {
    if (!isOpen || !game?.id || previewMode) {
      setMatchupStats(null);
      setMatchupStatsError(null);
      setMatchupStatsLoading(false);
      setLineHistory(null);
      setLineHistoryError(null);
      setLineHistoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setMatchupStatsLoading(true);
    setMatchupStatsError(null);
    setLineHistoryLoading(true);
    setLineHistoryError(null);

    api.getGameMatchupStats(game.id)
      .then((response) => {
        if (!cancelled) {
          setMatchupStats(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMatchupStatsError('Could not load season game log.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMatchupStatsLoading(false);
        }
      });

    api.getGameLineHistory(game.id)
      .then((response) => {
        if (!cancelled) {
          setLineHistory(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLineHistoryError('Could not load line movement.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLineHistoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, game?.id, previewMode]);

  const isPickWatched = useCallback((pickType) => {
    const pickId = `${game?.id}_${pickType}`;
    return watchedPicks.some(
      (pick) => pick.id === pickId && pick.weekNumber === displayedWeek
    );
  }, [game?.id, watchedPicks, displayedWeek]);

  const isPickLockedByOther = useCallback((pickType) => {
    if (!previewMode && !lockVisibility.showLocksForViewer) return false;
    if (!previewMode && !canSeeLocks() && !lockVisibility.hasReachedMaxPicks) return false;
    return gameLocks.some(
      (lock) => lock.pickType === pickType && lock.status === 'locked'
    );
  }, [gameLocks, canSeeLocks, lockVisibility, previewMode]);

  const handleSelectPick = useCallback((pickType, lockLine) => {
    if (pickType == null) {
      setSelectedPick(null);
      return;
    }
    setSelectedPick({ pickType, lockLine });
  }, []);

  const handleClose = useCallback(() => {
    setSelectedPick(null);
    onClose();
  }, [onClose]);

  const handleConfirmPick = useCallback((action) => {
    if (!selectedPick) return;
    onPickAction({
      gameId: game.id,
      pickType: selectedPick.pickType,
      lockLine: selectedPick.lockLine,
      action,
    });
    setSelectedPick(null);
    onClose();
  }, [selectedPick, game.id, onPickAction, onClose]);

  if (!isOpen || !game) return null;

  const isLockDisabled = !selectedPick
    || hasReachedMaxPicks
    || !futureWeekLockAllowed
    || isCheckingFutureWeek
    || !isGameLockable;

  const isFutureWeek = !futureWeekLockAllowed && displayedWeek > currentWeek;

  const renderConfirmActions = (inline = false) => {
    if (!isGameLockable || !selectedPick) return null;

    return (
      <>
        <div className={inline ? styles.confirmationActionsInline : styles.confirmationActions}>
          <button type="button" className={styles.watchButton} onClick={() => handleConfirmPick('watch')}>
            Watch
          </button>
          <button
            type="button"
            className={`${styles.lockButton} ${isLockDisabled ? styles.disabled : ''}`}
            onClick={() => handleConfirmPick('lock')}
            disabled={isLockDisabled}
          >
            {isCheckingFutureWeek ? 'Checking...' : 'Lock Now'}
          </button>
        </div>
        {isFutureWeek && !futureWeekLockAllowed && (
          <div className={styles.futureWeekMessage}>Future week locks are disabled</div>
        )}
      </>
    );
  };

  const renderOverviewLocks = () => {
    if (!showGameLocks) return null;

    return (
      <div className={`${styles.section} ${styles.locksSection}`}>
        <h3 className={styles.sectionTitle}>League Locks</h3>
        <GameDetailLocks
          game={game}
          gameLocks={gameLocks}
          displayedWeek={displayedWeek}
          userId={user?.id}
          previewMode={previewMode}
        />
      </div>
    );
  };

  const renderGameLogContent = () => (
    <GameDetailGameLog
      game={game}
      matchupStats={resolvedMatchupStats}
      loading={!previewMode && matchupStatsLoading}
      error={previewMode ? null : matchupStatsError}
    />
  );

  const renderSplitsContent = () => (
    <GameDetailSplits
      game={game}
      matchupStats={resolvedMatchupStats}
      loading={!previewMode && matchupStatsLoading}
      error={previewMode ? null : matchupStatsError}
    />
  );

  const renderLinesContent = () => (
    <GameDetailLines
      game={game}
      lineHistory={resolvedLineHistory}
      loading={!previewMode && lineHistoryLoading}
      error={previewMode ? null : lineHistoryError}
    />
  );

  const renderOverviewContent = (confirmInline = false) => (
    <>
      <GameDetailOverview game={game} matchupStats={resolvedMatchupStats} />
      <GameDetailPickGrid
        game={game}
        selectedPick={selectedPick}
        onSelectPick={handleSelectPick}
        hasReachedMaxPicks={hasReachedMaxPicks}
        isPickLockedByOther={isPickLockedByOther}
        isPickWatched={isPickWatched}
        isGameLockable={isGameLockable}
        confirmActions={confirmInline ? renderConfirmActions(true) : null}
      />
      {renderOverviewLocks()}
    </>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'gamelog':
        return renderGameLogContent();
      case 'splits':
        return renderSplitsContent();
      case 'lines':
        return renderLinesContent();
      case 'box':
        return <GameDetailBoxScore game={game} />;
      case 'weather':
        return <GameDetailWeather game={game} />;
      case 'overview':
      default:
        return renderOverviewContent(false);
    }
  };

  const renderMobileContent = () => (
    <>
      {renderOverviewContent(true)}
      {renderGameLogContent()}
      {renderSplitsContent()}
      {renderLinesContent()}
      <GameDetailBoxScore game={game} />
      <GameDetailWeather game={game} />
    </>
  );

  const headerStatus = game.game_status === 'in_progress'
    ? formatGamePeriod(game.clock, game.period)
    : game.game_status === 'completed'
      ? 'Final'
      : null;

  return (
    <>
      <button
        type="button"
        className={styles.modalBackdrop}
        onClick={handleClose}
        onKeyDown={(e) => e.key === 'Escape' && handleClose()}
        aria-label="Close modal"
      />
      <div className={styles.modalOverlay}>
        <dialog
          className={`${styles.modalContent} ${isMobile ? styles.mobile : styles.desktop}`}
          open={isOpen}
          onClose={handleClose}
          aria-labelledby="game-detail-title"
        >
          <div className={styles.modalHeader}>
            <div className={styles.modalHeaderMain}>
              <h2 id="game-detail-title" className={styles.modalTitle}>
                <span className={styles.teamHeader}>
                  {game.away_team.rank && <span className={styles.rankBadge}>#{game.away_team.rank}</span>}
                  {game.away_team.name}
                </span>
                <span className={styles.vs}> @ </span>
                <span className={styles.teamHeader}>
                  {game.home_team.name}
                  {game.home_team.rank && <span className={styles.rankBadge}>#{game.home_team.rank}</span>}
                </span>
              </h2>
              {headerStatus && (
                <span className={`${styles.headerGameStatus} ${game.game_status === 'in_progress' ? styles.headerLive : styles.headerFinal}`}>
                  {game.game_status === 'in_progress' && <span className={styles.liveDot} aria-hidden>●</span>}
                  {headerStatus}
                </span>
              )}
            </div>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close game details">
              ×
            </button>
          </div>

          {!isMobile && (
            <div className={styles.tabBar} role="tablist">
              {DESKTOP_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabActive : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.modalBody}>
            {isMobile ? renderMobileContent() : renderTabContent()}
          </div>

          {!isMobile && activeTab === 'overview' && renderConfirmActions(false)}
        </dialog>
      </div>
    </>
  );
};

GameDetailModal.propTypes = {
  game: PropTypes.object,
  displayedWeek: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPickAction: PropTypes.func.isRequired,
  hasReachedMaxPicks: PropTypes.bool,
  initialSelectedPick: PropTypes.oneOf(['home', 'away', 'over', 'under', null]),
  previewLocks: PropTypes.array,
  previewMode: PropTypes.bool,
  previewMatchupStats: PropTypes.object,
  previewLineHistory: PropTypes.object,
};

export default GameDetailModal;
